import { MINUS_SIGN } from '@staiter/i18n/format';

/**
 * F101 пака `tails-closeout` (С-I, NC-45; К5-14) — копирование показанных сумм в буфер обмена.
 *
 * Интерфейс показывает знак минус U+2212, а Excel и поля ввода читают «−1 000,00» как текст: сумма,
 * скопированная из таблицы, переставала быть числом. Глобальный обработчик `copy` приводит суммы в
 * выделении к виду, который Excel читает числом; выделение без сумм не трогает (браузер копирует сам).
 *
 * Замер Excel (ru-KZ, ввод с региональными настройками): «-1 000,00» и «-1 000,00 ₸» — число;
 * «−1 000,00 ₸» (U+2212), «-1 000,00 $», «-1 000,00 ₽» и разряды через NBSP — текст.
 *
 * Правило (круги 2–4 гейта, UX):
 * - у каждой суммы — ASCII-минус и обычный пробел в разрядах;
 * - знак валюты снимается, только когда валюта в выделении ОДНА и выделение — одно значение или
 *   строки таблицы (ячейки через табуляцию): тогда колонка уходит в Excel числами и СУММ сходится;
 * - при двух и более валютах знак остаётся у каждой суммы: чужая валюта уходит видимым текстом, и
 *   СУММ не сложит тенге с долларами молча;
 * - в обычном тексте (фраза, письмо) валюта остаётся — меняются только знак и пробелы.
 */
const CURRENCY_GLYPHS = '₸$€₽¥';
const SPACE_CHARS = ` ${String.fromCharCode(0x00a0, 0x2009, 0x202f)}`;
/**
 * Сумма как отдельное значение: начало строки или пробел/табуляция/скобка слева, конец или то же
 * справа. Даты («05.10.2026»), годы и коды не совпадают: после дробной части стоит граница.
 */
const AMOUNT_RE = new RegExp(
  `(^|[\\s(])([${MINUS_SIGN}+-]?\\d{1,3}(?:[${SPACE_CHARS}]\\d{3})*(?:[.,]\\d+)?)(?:[${SPACE_CHARS}]?([${CURRENCY_GLYPHS}]))?(?=$|[\\s)])`,
  'gmu',
);
const SPACES_RE = new RegExp(`[${String.fromCharCode(0x00a0, 0x2009, 0x202f)}]`, 'g');

export function minusToAscii(text: string): string {
  return text.split(MINUS_SIGN).join('-');
}

/** Одна сумма в виде, который Excel читает числом: ASCII-минус, обычный пробел в разрядах. */
function plainAmount(amount: string): string {
  return minusToAscii(amount.replace(SPACES_RE, ' '));
}

/** Разбор выделения: где искать суммы и снимать ли знак валюты; `null` — сумм нет. */
function analyse(selection: string): { source: string; strip: boolean } | null {
  const trimmed = selection.trim();
  const tabular = trimmed.includes('\t');
  const source = tabular || trimmed.includes('\n') ? selection : trimmed;

  const currencies = new Set<string>();
  let amounts = 0;
  let amountsLength = 0;
  for (const match of source.matchAll(AMOUNT_RE)) {
    amounts += 1;
    amountsLength += match[0].length - (match[1]?.length ?? 0);
    if (match[3]) currencies.add(match[3]);
  }
  if (amounts === 0) return null;

  const single = amounts === 1 && amountsLength === source.length;
  return { source, strip: currencies.size <= 1 && (single || tabular) };
}

/** Замена сумм в куске текста по решению `strip`, принятому для всего выделения. */
function replaceAmounts(text: string, strip: boolean): string {
  const next = text.replace(
    AMOUNT_RE,
    (_m, lead: string, amount: string, currency: string | undefined) =>
      lead + plainAmount(amount) + (currency && !strip ? ` ${currency}` : ''),
  );
  return minusToAscii(next);
}

/**
 * Что положить в буфер вместо выделения; `null` — оставить браузеру (менять нечего).
 * «−1 000,00 ₸» → «-1 000,00»; «Закупки\t−1 000,00 ₸\t500,00 ₸» → «Закупки\t-1 000,00\t500,00»;
 * «A\t−1 000,00 ₸\nB\t500,00 $» → «A\t-1 000,00 ₸\nB\t500,00 $»; «Итог −1 450,00 ₸» → «Итог -1 450,00 ₸».
 */
export function clipboardTextFor(selection: string): string | null {
  const parsed = analyse(selection);
  if (!parsed) return null;
  const out = replaceAmounts(parsed.source, parsed.strip);
  return out === selection ? null : out;
}

/**
 * С-I2 Р.9 (NC-45) — HTML выделения с той же заменой. Подмена одного `text/plain` стирала разметку:
 * таблица, вставленная в Word, теряла вид. Строки таблицы оборачиваются её предками до `<table>`:
 * голые `<tr>` Word и Excel читают текстом. Решение о знаке валюты — общее для всего выделения.
 */
export function clipboardHtmlFor(selection: Selection, strip: boolean): string | null {
  if (selection.rangeCount === 0) return null;
  const host = document.createElement('div');
  for (let i = 0; i < selection.rangeCount; i += 1) {
    const range = selection.getRangeAt(i);
    let node: Node = range.cloneContents();
    const common = range.commonAncestorContainer;
    const anchor =
      common.nodeType === Node.ELEMENT_NODE ? (common as Element) : common.parentElement;
    const table = anchor?.closest('table');
    if (anchor && table) {
      for (let cur: Element | null = anchor; cur; cur = cur.parentElement) {
        const shell = cur.cloneNode(false);
        shell.appendChild(node);
        node = shell;
        if (cur === table) break;
      }
    }
    host.appendChild(node);
  }
  const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT);
  for (let text = walker.nextNode(); text; text = walker.nextNode()) {
    text.nodeValue = replaceAmounts(text.nodeValue ?? '', strip);
  }
  return host.innerHTML;
}

/** С-I2 Р.9 (NC-45): выделение в поле ввода — дело поля; глобальный обработчик его не трогает. */
function isEditable(node: EventTarget | Element | null): boolean {
  if (!node || typeof (node as Element).closest !== 'function') return false;
  const el = node as HTMLElement;
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el.isContentEditable === true
  );
}

export function handleCopyMinus(e: ClipboardEvent): void {
  // С-I2 Р.9 (NC-45): копирование уже обработал компонент (свой буфер) — не перебивать.
  if (e.defaultPrevented) return;
  if (isEditable(e.target ?? null) || isEditable(document.activeElement)) return;
  const sel = typeof window !== 'undefined' ? window.getSelection() : null;
  const selection = sel?.toString() ?? '';
  if (!selection || !e.clipboardData) return;
  const parsed = analyse(selection);
  if (!parsed) return;
  const text = replaceAmounts(parsed.source, parsed.strip);
  if (text === selection) return;
  e.clipboardData.setData('text/plain', text);
  const html = sel ? clipboardHtmlFor(sel, parsed.strip) : null;
  if (html) e.clipboardData.setData('text/html', html);
  e.preventDefault();
}

let installed = false;

export function installMinusCopyHandler(target: Document = document): void {
  if (installed) return;
  target.addEventListener('copy', handleCopyMinus);
  installed = true;
}
